// ============================================================================
// 🔍 SEARCHBAR — Barra de Búsqueda con Debounce
// src/features/mensagens/components/lista/SearchBar.jsx
// ============================================================================

import React, { forwardRef, useCallback, useEffect, useRef } from 'react';
import { Search, X, Loader2 } from 'lucide-react';
import { PERFORMANCE } from '../../utils/constants';

const SearchBar = forwardRef(({ value, onChange, onClose, placeholder = 'Buscar...', loading = false }, ref) => {
  const debounceRef = useRef(null);

  const handleChange = useCallback((e) => {
    const val = e.target.value;
    // Debounce
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      onChange?.(val);
    }, PERFORMANCE.SEARCH_DEBOUNCE_MS);
  }, [onChange]);

  const handleClear = useCallback(() => {
    onChange?.('');
    if (ref?.current) ref.current.focus();
  }, [onChange, ref]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      if (value) {
        handleClear();
      } else {
        onClose?.();
      }
    }
  }, [value, handleClear, onClose]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  return (
    <div className="flex items-center gap-2 bg-card rounded-xl border border-gray-200 px-3 py-2.5 shadow-sm">
      {loading ? (
        <Loader2 size={18} className="text-gray-400 animate-spin flex-shrink-0" />
      ) : (
        <Search size={18} className="text-gray-400 flex-shrink-0" />
      )}
      <input
        ref={ref}
        type="text"
        defaultValue={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className="flex-1 text-sm text-gray-700 placeholder-gray-400 bg-transparent border-none outline-none"
        autoFocus
      />
      {value && (
        <button
          onClick={handleClear}
          className="p-0.5 rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
          aria-label="Limpar busca"
        >
          <X size={16} />
        </button>
      )}
      <button
        onClick={onClose}
        className="text-sm text-gray-400 hover:text-gray-600 font-medium px-1 transition-colors"
        aria-label="Fechar busca"
      >
        Cancelar
      </button>
    </div>
  );
});

SearchBar.displayName = 'SearchBar';
export { SearchBar };
export default SearchBar;